import type { ISpawnableObjectProperty } from '@interfaces/event';
import type { RealtimeEntityPropertyValue } from '@interfaces/realtimeEntities';
import { getKeyFromUrl } from '@helpers/assetUrls';

/** Formats the engine decodes for a texture. */
export const TEXTURE_ACCEPT = 'image/png,image/jpeg';

/** Prefix of the temporary key an upload lands on before a save moves it. */
const UPLOADS_KEY_PREFIX = 'uploads/';

/**
 * Tells whether a schema property carries an author-uploaded image.
 *
 * @param property - Schema entry for the property.
 * @returns Whether the property renders as an upload control.
 */
export const isTextureProperty = (
  property: ISpawnableObjectProperty,
): boolean => property.type === 'texture';

/**
 * Reads a texture value as the URL string it is.
 *
 * @param value - The stored property value, possibly absent.
 * @returns The URL, or `''` for an absent or non-string value.
 */
export const readTextureUrl = (
  value: RealtimeEntityPropertyValue | undefined,
): string => (typeof value === 'string' ? value : '');

/**
 * Tells whether a texture URL still points at the temporary upload key — the
 * file a save moves, and the one that dies with the session otherwise.
 *
 * @param url - Texture URL as the property holds it.
 * @returns Whether the object has to be saved for the image to persist.
 */
export const isUnsavedUploadUrl = (url: string): boolean =>
  getKeyFromUrl(url).startsWith(UPLOADS_KEY_PREFIX);

/**
 * Names the string properties whose stored key differs from the one that was
 * sent — the uploads a save moved under the entity's permanent prefix.
 *
 * Compared by S3 key rather than by URL: the backend strips the signature on
 * write and signs again on read, so the URL string differs on every round trip
 * while the key only changes when the file actually moved.
 *
 * @param sent - Property bag the save sent.
 * @param stored - Property bag the backend returned.
 * @returns The stored values to forward, by property name.
 */
export const collectRelocatedProperties = (
  sent: Record<string, RealtimeEntityPropertyValue> | undefined,
  stored: Record<string, RealtimeEntityPropertyValue> | undefined,
): Record<string, string> => {
  const relocated: Record<string, string> = {};

  Object.entries(stored ?? {}).forEach(([name, storedValue]) => {
    const sentValue = sent?.[name];

    if (typeof storedValue !== 'string' || typeof sentValue !== 'string') {
      return;
    }

    if (getKeyFromUrl(storedValue) !== getKeyFromUrl(sentValue)) {
      relocated[name] = storedValue;
    }
  });

  return relocated;
};
